import React, { type FC, useCallback } from 'react';
import { observer } from '@legendapp/state/react';
import { MarioBottomSheetFlatList } from '../../../../../utils';
import { ProductGiftContents } from '../../product-gift-contents/product-gift-contents';
import type { GiftSection } from '../gifts-selector-widget-contents.types';
import { useGiftSelection } from '../../../hooks/use-gift-selection.hook';
import { useCartItemGiftSelection } from '../../../hooks/use-cart-item-gift-selection.hook';
import { cartItemReferenceStoreAPI } from '@kidslabs/module-quote';
import type { SelectedGift } from '../../../types/gift-selection.types';
import { giftSelectionStoreAPI } from '../../../stores/simple-gift-selection-store.public-api';

/**
 * Props for RemainingGiftsSelectionComponent
 *
 * @interface RemainingGiftsSelectionComponentProps
 * @property {GiftSection} giftSection - Gift section containing product and available gifts data
 */
export interface RemainingGiftsSelectionComponentProps {
    /**
     * Gift section containing product information and sorted gifts data list
     * Used to display and manage gift selection for the specific product
     */
    giftSection: GiftSection;
}

/**
 * RemainingGiftsSelectionComponent
 *
 * **Purpose:**
 * A container component that displays the list of remaining available gifts within a bottom sheet context.
 * It provides controls to select additional gifts based on the remaining product quantity after initial selections.
 *
 * **Architecture:**
 * - **Container Component**: Handles business logic and state management
 * - **Observable**: Uses Legend-State observer for reactive updates
 * - **Integration**: Connects draft cart store with gift selection logic
 *
 * **Responsibilities:**
 * - Retrieves current product quantity from draft cart store
 * - Reuses SAME session as first selection (not creating new session)
 * - Passes selected gifts observable and mutation handlers to presentation component
 * - Manages SECOND selection phase for remaining gifts
 * - Shows only gifts that fit in remaining quantity (after first selection)
 * - When user selects gift, REPLACES existing second selection (not appends)
 *
 * **State Management:**
 * - Uses `cartItemReferenceStoreAPI.state.getQuantityStepperObservable()` for reactive product quantity
 * - Uses `useGiftSelection` hook with `createNewSession: false` to REUSE first selection session
 * - Passes observable `selectedGifts$` to ProductGiftContents for reactive rendering
 * - All gift selections (first + remaining) are stored in SINGLE session
 *
 * **Data Flow:**
 * 1. Receives `giftSection` prop with product and available gifts
 * 2. Retrieves current product quantity from draft cart store
 * 3. Reuses SAME session as first selection (merged gift selections)
 * 4. Passes state and handlers to ProductGiftContents presentation component
 * 5. Handles user interactions (select gift) - appends to first selection
 * 6. Shows only remaining gifts based on remaining product quantity
 * 7. When bottom sheet closes, ALL gifts (first + remaining) are persisted
 *
 * **Integration Points:**
 * - **Draft Cart Store**: Reads product quantity for gift allocation calculation
 * - **Gift Selection Hook**: Manages gift selection business logic with SHARED session
 * - **ProductGiftContents Component**: Renders the remaining gifts list UI
 * - **MarioBottomSheetFlatList**: Provides bottom sheet optimized list rendering
 * - **CurrentSelectedGiftsComponent**: Shares same session for unified gift state
 *
 * **Usage Context:**
 * Used within GiftsSelectorWidgetContents to display remaining available gifts
 * in a bottom sheet when user is adding/editing product with gift promotions.
 * This allows users to select additional gifts beyond their initial selection.
 *
 * @component
 * @category Gift Management
 * @subcategory Components
 *
 * @example
 * ```tsx
 * // Usage in GiftsSelectorWidgetContents
 * <RemainingGiftsSelectionComponent
 *   giftSection={{
 *     product: selectedProduct,
 *     sortedGiftsDataList: availableGifts
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Typical flow:
 * // 1. User opens bottom sheet to select additional gifts
 * // 2. RemainingGiftsSelectionComponent shows gifts not yet selected
 * // 3. User can select additional gifts based on remaining quantity
 * // 4. Changes are reflected reactively through Legend-State observable
 * ```
 *
 * @remarks
 * - This component uses `createNewSession: false` to REUSE first selection session
 * - It works alongside CurrentSelectedGiftsComponent in the SAME session
 * - The observer wrapper ensures automatic re-rendering when cart quantity changes
 * - Product quantity changes automatically update gift allocation limits
 * - Only shows gifts that fit in remaining quantity (showOnlyRemainingGifts: true)
 * - Selecting gift here REPLACES existing second selection (index 1)
 * - Maximum 2 gifts total: First selection (index 0) + Second selection (index 1)
 * - When bottom sheet closes, both first and second selections are persisted
 *
 * @see {@link ProductGiftContents} - Presentation component for gift list rendering
 * @see {@link useGiftSelection} - Hook managing gift selection business logic
 * @see {@link cartItemReferenceStoreAPI} - Cart item reference store public API
 * @see {@link GiftSection} - Type definition for gift section data structure
 * @see {@link CurrentSelectedGiftsComponent} - Companion component for showing selected gifts
 */
export const RemainingGiftsSelectionComponent: FC<RemainingGiftsSelectionComponentProps> = observer(
    ({ giftSection }) => {
        const sessionId = giftSection.sessionId;
        const {
            product,
            sortedGiftsDataList,
            productQuantity: productQuantityProp,
            cartItemReference,
            isReplacementMode = false,
        } = giftSection;

        // Determine if we're in cart context (editing existing cart item gifts)
        const isCartContext = !!cartItemReference;

        // Use productQuantity from props if provided (cart context), otherwise use cartItemReferenceStore (PDP context)
        const draftQuantity = cartItemReferenceStoreAPI.state.getQuantityStepperObservable().get();
        const counterValue = productQuantityProp ?? draftQuantity;

        /**
         * Intelligent Hook - sessionId as KEY
         * - PDP: reuse session from first selection (round 2)
         * - Cart: use cart item reference based session
         */
        const pdpGiftHook = useGiftSelection();
        const pdpSessionId = sessionId
            ? sessionId
            : (pdpGiftHook.getActiveSessionId(`${product?.id}`, 'product') ?? '');

        // Cart context: Use cart item reference based session
        const cartGiftSelection = useCartItemGiftSelection({
            cartItemReference: (cartItemReference ?? {
                cartItemReferenceId: '',
                productId: product?.id ?? 0,
                qty: counterValue,
                logics: [],
            }) as unknown as Parameters<typeof useCartItemGiftSelection>[0]['cartItemReference'],
            availableGifts: sortedGiftsDataList,
            isReplacementMode,
            sessionId: pdpSessionId,
        });

        // For PDP context: use intelligent hook with sessionId
        // Get observable and remaining quantity
        const pdpSelectedGifts$ = pdpGiftHook.getStoreObservable(pdpSessionId)?.selectedGifts;
        const pdpRemainingQty = pdpGiftHook.getRemainingQuantity(pdpSessionId, 2); // Round 2

        const handlePdpSelectGift = useCallback(
            (gift: SelectedGift) => {
                pdpGiftHook.selectGift(pdpSessionId, gift, 2); // Round 2
            },
            [pdpGiftHook, pdpSessionId]
        );

        // Use appropriate data based on context
        const selectedGifts$ = isCartContext ? cartGiftSelection.store$?.selectedGifts : pdpSelectedGifts$;
        const onSelectGift = isCartContext ? cartGiftSelection.selectGift : handlePdpSelectGift;
        const remainingProductQuantity = isCartContext ? counterValue : pdpRemainingQty;
        const replacementMode = isCartContext ? cartGiftSelection.isReplacementMode : isReplacementMode;

        // Auto-fill handler: update quantity and select the gift
        const handleAutoFillRequired = useCallback(
            (requiredQuantity: number, gift: SelectedGift) => {
                // 1. Notify parent to update quantity UI (stepper, etc.)
                giftSection.onAutoFillQuantity?.(requiredQuantity);

                // 2. Update gift selection session's productQuantity
                const currentSessionId = pdpSessionId;
                if (currentSessionId && giftSelectionStoreAPI.computed.sessionExists(currentSessionId)) {
                    giftSelectionStoreAPI.actions.updateSession(currentSessionId, {
                        productQuantity: requiredQuantity,
                    });
                }

                // 3. Now select the gift (quantity is updated, so it becomes eligible)
                const rule = gift.giftData?.rule;
                if (rule) {
                    const buyX = rule.discount_step || 1;
                    const getY = rule.discount_amount || 1;
                    const eligibleSets = Math.floor(requiredQuantity / buyX);
                    const maxFreeGifts = eligibleSets * getY;

                    const updatedGift: SelectedGift = {
                        ...gift,
                        quantity: maxFreeGifts,
                        giftSlots: maxFreeGifts,
                        quantityUsedByRule: eligibleSets * buyX,
                    };

                    onSelectGift(updatedGift);
                }
            },
            [giftSection, pdpSessionId, onSelectGift]
        );

        return (
            <ProductGiftContents
                FlatListComponent={
                    MarioBottomSheetFlatList as Parameters<typeof ProductGiftContents>[0]['FlatListComponent']
                }
                counterValue={counterValue}
                selectedGifts$={selectedGifts$}
                sortedGiftsDataList={sortedGiftsDataList}
                showOnlyRemainingGifts={true}
                onSelectGift={onSelectGift}
                remainingProductQuantity={remainingProductQuantity}
                isReplacementMode={replacementMode}
                sessionId={pdpSessionId}
                enableAutoFill={giftSection.enableAutoFill}
                onAutoFillRequired={handleAutoFillRequired}
            />
        );
    }
);

RemainingGiftsSelectionComponent.displayName = 'RemainingGiftsSelectionComponent';
